import z from "zod";

export const ChatIdParamsSchema = z.object({
  id: z.string(),
});

export const ChatIdMessageIdParamsSchema = z.object({
  id: z.string(),
  messageId: z.string(),
});

export const ForkChatBodySchema = z.object({
  messageId: z.string(),
});

export const ForkChatResponseSchema = z.object({
  newChatId: z.string(),
  title: z.string(),
  messagesReferenced: z.number(),
});

export const ImprovePromptBodySchema = z.object({
  prompt: z.string(),
  assetIds: z.array(z.string()).optional(),
  conceptIds: z.array(z.string()).optional(),
});

export type ImprovePromptBody = z.infer<typeof ImprovePromptBodySchema>;

const AgentSuggestionSchema = z.object({
  name: z.string(),
  displayName: z.string(),
});

const SkillSuggestionSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string(),
  scope: z.string().optional(),
  enabled: z.boolean(),
});

const CapabilitySuggestionsSchema = z.object({
  agents: z.array(AgentSuggestionSchema).optional(),
  skills: z.array(SkillSuggestionSchema).optional(),
});

// Single improved prompt result. Carries no mode: the classifier is the single
// owner of routing, so an improved prompt is routed like any other.
const ImprovedPromptResultSchema = z.object({
  content: z.string(),
  summary: z.string(),
  capabilities: CapabilitySuggestionsSchema.optional(),
});

export const ImprovePromptResponseSchema = z.object({
  prompts: z.array(ImprovedPromptResultSchema),
});

export type ImprovePromptResponse = z.infer<typeof ImprovePromptResponseSchema>;

export const AutocompleteBodySchema = z.object({
  draft: z.string(),
  candidates: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
  generate: z.boolean().optional(),
  warm: z.boolean().optional(),
  // The chat's last completed turn, so a generated completion reads as a follow-up to it.
  conversation: z.object({ lastPrompt: z.string(), lastAnswer: z.string() }).optional(),
});

// `text` is the whole suggested prompt: it starts with the draft verbatim and the ghost is what
// follows. `id` names the candidate it came from and is absent on a generated completion.
const AutocompleteSuggestionSchema = z.object({
  id: z.string().optional(),
  text: z.string(),
  score: z.number(),
});

export type AutocompleteSuggestion = z.infer<typeof AutocompleteSuggestionSchema>;

export const ClassifyModeBodySchema = z.object({
  prompt: z.string(),
});

export const ClassifyModeResponseSchema = z.object({
  mode: z.enum(["fast", "expert", "research"]),
  confidence: z.number(),
});

export type ClassifyModeResponse = z.infer<typeof ClassifyModeResponseSchema>;

// `mode` is set whenever the server's candidate judge ran (more than one candidate continued the
// draft), with or without a suggestion. It stands in for a classify call.
export const AutocompleteResponseSchema = z.object({
  suggestion: AutocompleteSuggestionSchema.nullable(),
  mode: ClassifyModeResponseSchema.optional(),
});
